import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppRoutesConstant } from '@constants/app-routes.costant';
import { SessionStorageEnum } from '@enums/session-storage.enum';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ExtrasInfo } from '@shared/services/extras/_interfaces_/extras-info.interface';
import { StepperBarStep } from '@shared/services/extras/_interfaces_/extras-steps.interface';
import { selectSelectedOrderDossierNumber } from '@store/selected-order/selected-order.selectors';
import { take } from 'rxjs/operators';
import { BaggageAccordionComponent } from '../baggage-accordion/baggage-accordion.component';
import { LoungeAccordionComponent } from '../lounge-accordion/lounge-accordion.component';
import { ExtraProductsEnum } from '@enums/extra-products.enum';
import { AirportAssistanceAccordionComponent } from '../airport-assistance-accordion/airport-assistance-accordion.component';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { SessionStorageService } from '@shared/services/session-storage/session-storage.service';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';
import { selectCommonOrderReference } from '@store/common-order/common-order.selectors';

@Component({
  selector: 'app-create-extras',
  templateUrl: './create-extras.component.html',
  styleUrls: ['./create-extras.component.scss'],
})
export class CreateExtrasComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private _localStorageService = inject(LocalStorageService);
  private _sessionStorage = inject(SessionStorageService);
  private activatedRoute = inject(ActivatedRoute);
  private cd = inject(ChangeDetectorRef);
  private store = inject(Store);
  culture: string;

  @Input() extrasInfoData: ExtrasInfo;
  @ViewChild(BaggageAccordionComponent) baggageAccordionComponent: BaggageAccordionComponent;
  @ViewChild(LoungeAccordionComponent) loungeAccordionComponent: LoungeAccordionComponent;
  @ViewChild(AirportAssistanceAccordionComponent) airportAssistanceComponent: AirportAssistanceAccordionComponent;
  private dossierNumber: string;
  private orderReference: string;
  extrasSteps: Array<StepperBarStep>;
  isNextButtonDisabled: boolean = true;
  isAirportAssistanceDisabled: boolean = true;
  isLoungeDisabled: boolean = true;
  isBaggageDisabled: boolean = true;
  expandExtras: string;

  constructor(private translate: TranslateService, private cdRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.getOrderReference();

    this.translate.onLangChange.subscribe(() => {
      this.updateExtrasSteps();
      this.culture = this.translate.currentLang;
    });
    this.updateExtrasSteps();
    this.setAccordionOpenOption();
  }

  public setAccordionOpenOption() {
    const passengerExtras = this.extrasInfoData?.passengers[0]?.passengerExtras;
    const expandExtras = passengerExtras.find((extras) => extras?.options?.length > 0);
    this.expandExtras = expandExtras.type;
  }

  private getOrderReference(): void {
    this.store
      .select(selectCommonOrderReference)
      .pipe(take(1))
      .subscribe((orderReference) => (this.orderReference = orderReference || ''));
  }

  updateExtrasSteps(): void {
    this.extrasSteps = [
      { stepName: this.translate.instant('STEPBAR_EXTRAS.STEP_1') },
      { stepName: this.translate.instant('STEPBAR_EXTRAS.STEP_2') },
    ];
    this.cdRef.detectChanges();
  }

  ngAfterViewInit(): void {
    this.checkBaggageSelection();
    this.checkLoungeSelection();
    this.checkAirportAssistanceSelection();
  }

  checkBaggageSelection(isSelected?: boolean): void {
    if (isSelected !== undefined) {
      this.isBaggageDisabled = !isSelected;
    } else if (this.baggageAccordionComponent) {
      this.isBaggageDisabled = !this.baggageAccordionComponent.isAnyBaggageSelected();
    }
    this.enableNextButton();
    this.cd.detectChanges();
  }

  checkLoungeSelection(isSelected?: boolean): void {
    const isLoungeSelected = isSelected ?? this.loungeAccordionComponent?.isAnyLoungeSelected() ?? false;
    this.isLoungeDisabled = !isLoungeSelected;
    this.enableNextButton();
    this.cd.detectChanges();
  }

  checkAirportAssistanceSelection(isSelected?: boolean): void {
    const isAirportAssistanceSelected = isSelected ?? this.airportAssistanceComponent?.isAnyAirportAssistanceSelected() ?? false;
    this.isAirportAssistanceDisabled = !isAirportAssistanceSelected;
    this.enableNextButton();
    this.cd.detectChanges();
  }

  enableNextButton(): void {
    this.isNextButtonDisabled = this.isBaggageDisabled && this.isLoungeDisabled && this.isAirportAssistanceDisabled;
  }

  navigateToOrderOverViewPage(): void {
    if (this.orderReference) {
      this.router.navigateByUrl(`${UrlPathnameEnum.CommonOrder}/${this.orderReference}`);
    }
  }

  checkLoungeAvailability(): boolean {
    if (this.extrasInfoData) {
      if (this.extrasInfoData.passengers[0].passengerExtras[ExtraProductsEnum.lounge].options.length > 0) {
        return true;
      }
      return false;
    }
  }

  checkAirportAssistanceAvailability(): boolean {
    if (this.extrasInfoData) {
      if (this.extrasInfoData.passengers[0].passengerExtras[ExtraProductsEnum.airportAssistance].options.length > 0) {
        return true;
      }
    }
    return false;
  }

  navigateToOverviewPage(): void {
    if (!this.isNextButtonDisabled) {
      const queryParams = this.activatedRoute.snapshot.queryParams;
      sessionStorage.setItem(SessionStorageEnum.CreatePageRouteQueryParams, JSON.stringify(queryParams));
      this.router.navigate([UrlPathnameEnum.ExtrasPageOverview]);
    }
  }
}
